import React from 'react';
import styled, { keyframes } from 'styled-components';

// Define the animation keyframes for the slide-in text
const slidein = keyframes`
  0% { margin-left: -1000px; }
  20% { margin-left: -600px; }
  35% { margin-left: 0px; }
  100% { margin-left: 0px; }
`;

const HomeContainer = styled.div`
  position: relative;
  height: 720px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.background};
  transition: background 0.5s ease;
  padding: 4px 4px; /* Add padding to prevent content from touching the edges */
`;

const NameSection = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.textColor};
  overflow: hidden;
  white-space: nowrap;
  display: inline-block;

  /* Space for the button */
  padding-bottom: 50px;

  @media (max-width: 900px) {
    font-size: 16px;
  }

  @media (max-width: 400px) {
    font-size: 17px;
    b, strong {
    font-size: 17px;
  }
  }
`;

const AnimatedText = styled.h1`
  animation: ${slidein} 5s infinite;

  @media (max-width: 900px) {
    font-size: 26px;
  }

  @media (max-width: 400px) {
    font-size: 22px;
  }
`;

// Button styles
const Button = styled.div`
  --width: 220px;
  --height: 52px;
  --tooltip-height: 35px;
  --tooltip-width: 160px;
  --gap-between-tooltip-to-button: 18px;
  --button-color: #07374A;
  --tooltip-color: #fff;
  width: var(--width);
  height: var(--height);
  background: var(--button-color);
  position: relative;
  text-align: center;
  border-radius: 0.45em;
  font-family: Poppins,Sans-serif;
  transition: background 0.3s;
  margin: 0 auto; /* Center the button */

  @media (max-width: 900px) {
    --width: 220px;
    --height: 46px;
  }

  @media (max-width: 400px) {
    --width: 220px;
    --height: 46px;
  }
`;

const ButtonWrapper = styled.div`
  overflow: hidden;
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  color: #fff;
`;

const Text = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  transition: top 0.5s;
`;

const Icon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 100%;
  transition: top 0.5s;
  color: #fff;
`;

const Svg = styled.svg`
  width: 24px;
  height: 24px;

  @media (max-width: 900px) {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 400px) {
    width: 18px;
    height: 18px;
  }
`;

const ButtonTooltip = styled(Button)`
  &:before {
    position: absolute;
    content: attr(data-tooltip);
    width: var(--tooltip-width);
    height: var(--tooltip-height);
    background-color: var(--tooltip-color);
    font-size: 0.9rem;
    color: #111;
    border-radius: .25em;
    line-height: var(--tooltip-height);
    bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) + 10px);
    left: calc(50% - var(--tooltip-width) / 2);
    opacity: 0;
    visibility: hidden;
    transition: all 0.5s;

    @media (max-width: 900px) {
      font-size: 0.8rem;
      bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) + 8px);
    }

    @media (max-width: 400px) {
      font-size: 0.7rem;
      bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) + 6px);
    }
  }

  &:after {
    position: absolute;
    content: '';
    width: 0;
    height: 0;
    border: 10px solid transparent;
    border-top-color: var(--tooltip-color);
    left: calc(50% - 10px);
    bottom: calc(100% + var(--gap-between-tooltip-to-button) - 10px);
    opacity: 0;
    visibility: hidden;
    transition: all 0.5s;

    @media (max-width: 900px) {
      border-width: 8px;
      bottom: calc(100% + var(--gap-between-tooltip-to-button) - 8px);
    }

    @media (max-width: 400px) {
      border-width: 6px;
      bottom: calc(100% + var(--gap-between-tooltip-to-button) - 6px);
    }
  }

  &:hover {
    background: #005f73;
  }

  &:hover .text {
    top: -100%;
    font-family: Poppins,Sans-serif;
  }

  &:hover .icon {
    top: 0;
  }

  &:hover:before,
  &:hover:after {
    opacity: 1;
    visibility: visible;
  }

  &:hover:after {
    bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) - 20px);

    @media (max-width: 900px) {
      bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) - 16px);
    }

    @media (max-width: 400px) {
      bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) - 12px);
    }
  }

  &:hover:before {
    bottom: calc(var(--height) + var(--gap-between-tooltip-to-button));

    @media (max-width: 900px) {
      bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) - 4px);
    }

    @media (max-width: 400px) {
      bottom: calc(var(--height) + var(--gap-between-tooltip-to-button) - 8px);
    }
  }
`;

const Home = () => {
  return (
    <HomeContainer id="home-page">
      <NameSection>
        <AnimatedText>Coding & Web Dev Enthusiast</AnimatedText>
        &nbsp;
        <p>        &nbsp;
        </p>
        <p style={{ fontSize: "18px" }}> <b>Virtual Intern @Deloitte</b></p>
        <p style={{ fontSize: "18px" }}><b>Campus Ambassador @Coding Ninjas</b></p>
        &nbsp;
        <p>        &nbsp;
        </p>
        <p>B.Tech CSE with Spl. in <b>Fullstack AI</b></p>
        <p style={{ fontSize: "18px" }}><b>University of Petroleum & Energy Studies</b></p>
        <p>        &nbsp;
        </p>
        <p>        &nbsp;
        </p>
        <p>        &nbsp;
        </p>
        <a href="https://acrobat.adobe.com/id/urn:aaid:sc:AP:e1622758-ab13-41b2-ad3c-006c9a219427" download>
          <ButtonTooltip data-tooltip="Size : 87kb">
            <ButtonWrapper>
              <Text className="text">Download My Resume</Text>
              <Icon className="icon">
                <Svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24">
                  <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15V3m0 12l-4-4m4 4l4-4M2 17l.621 2.485A2 2 0 0 0 4.561 21h14.878a2 2 0 0 0 1.94-1.515L22 17"></path>
                </Svg>
              </Icon>
            </ButtonWrapper>
          </ButtonTooltip>
        </a>
      </NameSection>
    </HomeContainer>
  );
};

export default Home;
